// based imports and stores
import { computed } from 'vue';
import { useAuthStore } from '@/stores/auth';
import axios from 'axios';

class ApiService {

  constructor(baseURL) {
    this.api = axios.create({
      baseURL: baseURL ?? import.meta.env.VITE_API_URL,
      timeout: 25000
    });

    // Interceptor per aggiungere il token JWT
    this.api.interceptors.request.use(config => {
      // Se la richiesta non deve saltare l'autenticazione
      if (!config.skipAuth) {
        const authStore = useAuthStore();
        const token = authStore.token;
        

        if (token) {
         
          config.headers.Authorization = `Bearer ${token}`;
        } else {
          console.warn('Token non trovato per richiesta autenticata');
        }
      } else {
      }
      
      return config;
    }, error => {
      console.error('Errore nel request interceptor:', error);
      return Promise.reject(this.formatError(error));
    });

    // Interceptor per gestire le risposte
    this.api.interceptors.response.use(
      response => {
        
        // Se la risposta è valida, restituisci i dati
        if (response.data?.success === true || response.data?.data) {
          return response.data;
        }
        
        console.warn('Risposta non valida dal server:', response.data);
        return Promise.reject(this.formatError({
          message: 'Risposta non valida dal server'
        }));
      },
      error => {
        console.error('Response error interceptor:', {
          status: error.response?.status,
          url: error.config?.url,
          code: error.code,
          message: error.message
        });

        // Gestione specifica per errori 401
        if (error.response?.status === 401) {
          const authStore = useAuthStore();
          const currentPath = window.location.pathname;
          const isLoginContext = currentPath.includes('login') || 
                             (error.config?.url && error.config.url.includes('login'));
          
          
          // Non eseguiamo il logout se:
          // 1. Siamo in un contesto di login
          // 2. La richiesta stessa è per il login
          // 3. La richiesta è marcata come skipAuth
          if (!isLoginContext && !error.config?.skipAuth) {
            authStore.logout();
          } else {
          }
        }

        return Promise.reject(this.formatError(error));
      }
    );

  }

  // Nuovo metodo per formattare gli errori in modo consistente
  formatError(error) {

    // Se è già un errore formattato, restituiscilo
    if (error.isFormatted) {
      return error;
    }

    const formattedError = {
      success: false,
      isFormatted: true,
      status: error.response?.status,
      error: error.response?.data?.message || error.message || 'Errore sconosciuto'
    };

    // Gestione specifica per errori di rete
    if (error.code === 'ECONNABORTED') {
      console.warn('Richiesta abortita per timeout');
      formattedError.error = 'La richiesta è scaduta. Riprova.';
      return formattedError;
    }

    return formattedError;
  }

  async request(method, endpoint, data = null, config = {}) {
    
    try {
      const response = await this.api[method](endpoint, data, config);
      return response;
    } catch (error) {
      console.error(`Errore nella richiesta ${method.toUpperCase()} a ${endpoint}:`, error);
      throw this.formatError(error);
    }
  }

  async get(endpoint, config = {}) {
    return this.request('get', endpoint, null, config);
  }

  async post(endpoint, data, config = {}) {
    return this.request('post', endpoint, data, config);
  }

  async put(endpoint, data) {
    return this.request('put', endpoint, data);
  }

  async delete(endpoint) {
    return this.request('delete', endpoint);
  }

  async postFile(endpoint, data, options = {}) {
    const mergedOptions = {
      headers: {
        'Content-Type': 'multipart/form-data',
        ...options.headers
      }
    };
    return this.request('post', endpoint, data, mergedOptions);
  }

  setBaseURL(newBaseURL) {
    this.api.defaults.baseURL = newBaseURL;
  }

  setHeaders(headers) {
    this.api.defaults.headers = { ...this.api.defaults.headers, ...headers };
  }
  
}

const HttpService = new ApiService(import.meta.env.VITE_API_URL);

export default HttpService;
